'use client';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { getKycConfig, getKycStatus, submitKyc } from '@/lib/api/kyc/client';
import type { KycConfig, KycStatusResponse, KycSubmitFiles } from '@/lib/api/kyc/types';
import { placeMarketOrder } from '@/lib/api/order/client';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { FEATURE_FLAGS } from '@/lib/config/features';
// Relative on purpose: HeaderV2 pulls this into the trade zone, where `@/lib/api/history` is
// trade's own module without these exports.
import { fetchDealsOpenPositions } from '../history/client';
import type { DealsOpenPositionItem } from '../history/types';

export const kycStatusKey = ['kyc', 'status'] as const;
export const kycPositionsKey = ['kyc', 'open-positions'] as const;
export const kycConfigKey = ['kyc', 'config'] as const;

/** Result of closing all open positions in one action. */
export interface CloseAllResult {
  total: number;
  closed: number;
  failed: number;
}

export function useKycStatus() {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();

  return useQuery<KycStatusResponse>({
    queryKey: kycStatusKey,
    queryFn: () => getKycStatus(locale),
    enabled: FEATURE_FLAGS.ENABLE_KYC && isAuthenticated,
    staleTime: 30_000,
    // Poll while a submission is being verified so the screen advances on its own.
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 4000 : false),
  });
}

/**
 * Lists, document rules and limits for the wizard — changes rarely, so it is cached long. Picking
 * the issuing country refetches with the types it accepts; the previous config stays on screen.
 */
export function useKycConfig(issuing?: string) {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();
  const { data, isLoading } = useQuery<KycConfig>({
    queryKey: [...kycConfigKey, locale, issuing ?? ''],
    queryFn: () => getKycConfig(locale, issuing),
    enabled: FEATURE_FLAGS.ENABLE_KYC && isAuthenticated,
    staleTime: 60 * 60_000,
    placeholderData: keepPreviousData,
  });
  return { config: data, isLoading };
}

export function useSubmitKyc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      files: KycSubmitFiles;
      declared?: string;
      mobileSessionId?: string;
      screenTimings?: string;
    }) => submitKyc(vars.files, vars.declared, vars.mobileSessionId, vars.screenTimings),
    onSuccess: () => {
      // Flip to PENDING immediately so the screen shows "processing" straight away (no flash of the
      // pre-submit CTA) while the real status — PENDING → APPROVED / REJECTED — is refetched.
      queryClient.setQueryData<KycStatusResponse>(kycStatusKey, (old) =>
        old ? { ...old, status: 'PENDING' } : old,
      );
      void queryClient.invalidateQueries({ queryKey: kycStatusKey });
    },
  });
}

/**
 * Close ALL open positions in one action, for a KYC-restricted user who may only close out.
 * Fetches the user's open positions and places a market SELL for the available quantity of each
 * (this reuses the normal order path — the KYC guard permits SELL/closing). Partial failures are
 * expected (thin books, dust) and reported back rather than throwing.
 */
export function useCloseAllPositions() {
  const queryClient = useQueryClient();
  const locale = useLocale();

  return useMutation<CloseAllResult>({
    mutationFn: async () => {
      const page = await fetchDealsOpenPositions({ size: 200, lang: locale });
      const positions = page.items.filter((p) => p.availableSharesCount > 0);
      const results = await Promise.allSettled(
        positions.map((p) =>
          placeMarketOrder({
            eventId: p.eventId,
            eventOutcomeId: p.eventOutcomeId,
            baseAssetId: p.assetId, // the held YES/NO token
            quoteAssetId: '', // ignored by the market endpoint (server forces USDC)
            side: 'SELL',
            shares: p.availableSharesCount,
          }),
        ),
      );
      const closed = results.filter((r) => r.status === 'fulfilled').length;
      return { total: results.length, closed, failed: results.length - closed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kycStatusKey });
      void queryClient.invalidateQueries({ queryKey: kycPositionsKey });
      void queryClient.invalidateQueries({ queryKey: ['deals-open-positions'] });
      void queryClient.invalidateQueries({ queryKey: ['user-balances'] });
    },
  });
}

/** The user's open positions, for the KYC restriction screen (full info, close each individually). */
export function useKycOpenPositions() {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();

  return useQuery<DealsOpenPositionItem[]>({
    queryKey: kycPositionsKey,
    queryFn: async () => {
      const page = await fetchDealsOpenPositions({ size: 200, lang: locale });
      return page.items;
    },
    enabled: isAuthenticated,
    staleTime: 15_000,
  });
}

/**
 * Close ONE open position: a market SELL of its available quantity (reuses the normal order path —
 * the KYC guard permits closing). Invalidates the positions list + status on success.
 */
export function useClosePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (position: DealsOpenPositionItem) =>
      placeMarketOrder({
        eventId: position.eventId,
        eventOutcomeId: position.eventOutcomeId,
        baseAssetId: position.assetId,
        quoteAssetId: '', // ignored by the market endpoint (server forces USDC)
        side: 'SELL',
        shares: position.availableSharesCount,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kycPositionsKey });
      void queryClient.invalidateQueries({ queryKey: kycStatusKey });
      void queryClient.invalidateQueries({ queryKey: ['deals-open-positions'] });
      void queryClient.invalidateQueries({ queryKey: ['user-balances'] });
    },
  });
}
